import { ReactNode } from 'react';
import { RouteObject } from 'react-router-dom';

import { COMPANY_TAB_ROLES, type CompanyTabPath } from '@constants';
import { AccessDenied, NotFound } from '@entities';
import { LayoutCompany, LayoutProtected, LayoutPublic } from '@layouts';
import {
  AccessMap,
  AccountNotLinked,
  AccountingUsage,
  Accounts,
  BalanceDiscrepancies,
  Billing,
  Companies,
  CompanyInfo,
  CompanyTeam,
  DocumentDetails,
  DocumentExpenseDetails,
  Documents,
  Incorporation,
  Incorporation_old,
  Incorporations,
  Legal,
  Login,
  MonthlyInvoices,
  Newsletter,
  Onboarding,
  Payslips,
  Processes,
  Settings,
  Taxes,
  TransactionDetails,
  Transactions,
} from '@pages';

type Route = Omit<RouteObject, 'children'> & {
  roles: '*' | string[];
  children?: Route[];
};

/**
 * One tab of the company workspace. Path and roles are both derived from the suffix, so a route
 * cannot end up guarded by the roles of a different tab: `COMPANY_TAB_ROLES` (`@constants`,
 * shared/constants/companyTabs.ts) is the single source LayoutCompany's tab bar reads too, and a
 * visible tab that only leads to Access denied was the SUP-251 symptom [ENG-1197]. Update the
 * roles there, not here.
 */
const companyTab = (suffix: CompanyTabPath, element: ReactNode): Route => ({
  path: `/companies/:companyId${suffix}`,
  element,
  roles: COMPANY_TAB_ROLES[suffix],
});

// `''` is the tab at /companies/:companyId itself — an explicit path rather than `index: true`,
// which react-router ignores as soon as a path is given.
const companyTabRoutes: Route[] = [
  companyTab('', <Onboarding />),
  companyTab('/info', <CompanyInfo />),
  companyTab('/documents', <Documents />),
  companyTab('/documents/bob50/:id', <DocumentExpenseDetails />),
  companyTab('/documents/:id', <DocumentDetails />),
  companyTab('/transactions', <Transactions />),
  companyTab('/transactions/:id', <TransactionDetails />),
  companyTab('/accounts', <Accounts />),
  companyTab('/incorporation_old', <Incorporation_old />),
  companyTab('/incorporation', <Incorporation />),
  companyTab('/legal', <Legal />),
  companyTab('/legal/:id', <Legal />),
  companyTab('/taxes', <Taxes />),
  companyTab('/team', <CompanyTeam />),
  companyTab('/billing', <Billing />),
  companyTab('/billing/:billingId', <Billing />),
  companyTab('/one-time-billing/:oneTimeBillingId', <Billing />),
  companyTab('/processes', <Processes />),
  companyTab('/payslips', <Payslips />),
  companyTab('/payslips/:id', <Payslips />),
];

const allRoutes: Route[] = [
  {
    path: '/',
    element: <LayoutProtected />,
    roles: ['admin', 'kyc', 'accountant'],
    children: [
      {
        /**
         * The legacy Chats & Tasks page is gone — Pulse has taken it over — but its URLs are not:
         * the CSMs have task links in their history, and a Corporate Documents row carried a "From
         * chat" badge pointing at one until this change. With no route at all those URLs match
         * nothing, React Router raises a 404 to `AppRouter`'s root `errorElement`, and
         * `ErrorFallback` both shows the developer-facing "Unexpected Application Error!" screen
         * and reports a *fatal* to Sentry — once per render, for as long as the bookmarks live.
         *
         * So the path keeps a route whose only job is to resolve. `roles: '*'` because a page that
         * no longer exists is not one to be denied access to. In practice the element below is the
         * backstop rather than what renders: no `accessMapping` fragment matches `/chat-tasks` any
         * more, so `LayoutProtected`'s own gate reaches Not found first — which is the same answer,
         * and the reason this route only has to exist, not to be reached.
         */
        path: '/chat-tasks/*',
        element: <NotFound />,
        roles: '*',
      },
      {
        path: '/monthly-invoices',
        element: <MonthlyInvoices />,
        // Admin only: the page's only API — GET /admin/billing_periods/unapproved and
        // PATCH /:id/approve — is @AdminRoles(Admin) on the backend, so any other role
        // got the page and a 403 instead of data [ENG-1197].
        roles: ['admin'],
      },
      {
        path: '/settings',
        element: <Settings />,
        roles: ['admin'],
      },
      {
        path: '/access',
        element: <AccessMap />,
        roles: ['admin'],
      },
      {
        path: '/newsletter',
        element: <Newsletter />,
        roles: ['admin'],
      },
      {
        // The CSM provisioning queue, read from Master Data. Admin only: the action behind it
        // creates a tenant schema and portal accounts, and the backend controller is
        // @AdminRoles(AdminRole.Admin) — a route admitting a role the API refuses only produces a
        // sidebar entry leading to a 403 page [ENG-1197]. Note that the substring 'incorporation'
        // in kyc's and support's accessMapping lists matches this path in LayoutProtected, so this
        // exact roles list is what actually keeps them out; the sidebar item carries its own
        // explicit roles for the same reason.
        path: '/incorporations',
        element: <Incorporations />,
        roles: ['admin'],
      },
      {
        path: '/accounting-usage',
        element: <AccountingUsage />,
        // Admin only, like GET/PUT /admin/accounting_usage (@AdminRoles(Admin)) — CSMs work under
        // the admin role. A route admitting a role the API refuses only produces a sidebar entry
        // leading to a 403 page [ENG-1197].
        roles: ['admin'],
      },
      {
        path: '/balance-discrepancies',
        element: <BalanceDiscrepancies />,
        // The same roles as GET /admin/balance_portfolio, which is
        // @AdminRoles(Admin, Accountant, Support) — a route admitting a role the API refuses only
        // produces a sidebar entry leading to a 403 page [ENG-1197]. Accountants above all: they are
        // the ones carrying enough companies for the per-company view to be unusable.
        roles: ['admin', 'accountant', 'support'],
      },
      {
        path: '/companies',
        element: <LayoutCompany />,
        roles: ['admin', 'kyc', 'accountant'],
        children: [
          {
            path: '/companies',
            element: <Companies />,
            roles: ['admin', 'kyc', 'accountant'],
          },
          {
            path: '/companies/:companyId',
            roles: ['admin', 'kyc', 'accountant'],
            children: companyTabRoutes,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <LayoutPublic />,
    roles: '*',
    children: [
      {
        index: true,
        path: '/login',
        roles: '*',
        element: <Login />,
      },
    ],
  },
  // Top level and outside LayoutPublic on purpose. The realm session is valid when this renders, so
  // LayoutPublic would bounce it to /companies, fail with the same 401 and come straight back —
  // exactly the loop this page exists to break.
  {
    path: '/account-not-linked',
    roles: '*',
    element: <AccountNotLinked />,
  },
];

export const getRoutes = (
  role?: string,
  routes: Route[] = allRoutes
): Route[] =>
  routes.map((route) => ({
    ...route,
    children: route.children?.map((child) => {
      let element = child.element;
      if (
        child.roles === '*' ||
        (role !== undefined && child.roles.includes(role))
      ) {
        element = child.element;
      } else {
        element = <AccessDenied />;
      }

      return {
        ...child,
        element,
        children: child.children ? getRoutes(role, child.children) : undefined,
      };
    }),
  }));
